import { Button } from "@stockline/ui/components/button";
import { Pencil, X, Loader2, XCircle, Check, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import type { ModifyBufferApi } from "../../hooks/useModifyBuffer";
import { type PreOrder, PreOrderStatus } from "@/app/api/types/preOrder";
import { ModifyErrorPopover } from "./ModifyErrorPopover";
import { useAiPreOrderActions } from "@/app/ai-pre-orders/hooks/useAiPreOrderActions";
import { collectAiPaths, hasAnyPendingAi } from "../../services/recapService";
import { useGoToPreOrder } from "../../hooks/useGoToPreOrder";
import { computePostAcceptTarget } from "../../services/aiAcceptVisibility";
import { useModifyBufferStore } from "../../stores/modifyBufferStore";
import { useGlobalPartners } from "@/query/hooks/useUnifiedCache";
import { usePreOrderLockOptional } from "../../contexts/PreOrderLockContext";
import { useMemo } from "react";
import { canMutatePreOrder } from "../../services/canMutatePreOrder";
import { useProposalViewer } from "@/app/ai-pre-orders/hooks/useProposalViewer";

/**
 * `"default"` — the compact h-6 bar used inside recap list cards and the
 * focus flows-card header. `"hero"` — the larger h-10 treatment used when
 * the bar is hoisted into the focus page's hero action row (AI proposals):
 * buttons match the surrounding hero controls, Decline reads **Delete**
 * (red), and the modify-mode accept reads **Accept & modify** (orange).
 */
type ControlsVariant = "default" | "hero";

const BTN_SIZE: Record<ControlsVariant, string> = {
  default: "h-6 gap-1 text-xs px-2",
  hero: "h-10 gap-1.5 text-sm px-3.5",
};
const ICON_SIZE: Record<ControlsVariant, string> = {
  default: "h-3 w-3",
  hero: "h-4 w-4",
};

interface Props {
  preOrder: PreOrder;
  buffer: ModifyBufferApi;
  /** Visual treatment — see `ControlsVariant`. Defaults to `"default"`. */
  variant?: ControlsVariant;
}

/**
 * Modify-mode entry button + flush bar, plus inline AI proposal
 * accept/decline.
 *
 * Save is the implicit accept gesture. Every Save click (modify-mode AND
 * the inline "Accept AI changes" button) goes through `buffer.flushSave`
 * which runs the client-side `aiResolveLocally` resolver to merge
 * pending `ai_suggestions[]` blocks with the user's buffered edits, then
 * posts the merged payload to V2 push-batch as a single CRUD operation.
 * Backend is generic CRUD; AI resolution semantics live entirely in the
 * frontend (no drift between server and client logic). Decline goes
 * through the same resolver with `decision="decline"` and ships in the
 * same single push-batch — no separate decline endpoint, no column
 * writes for a pure decline.
 *
 * Outside modify mode:
 *  - PENDING / AI_PROPOSAL with no AI signal: no button — first cell edit
 *    auto-enters modify mode.
 *  - PENDING / AI_PROPOSAL with pending AI: violet **Accept AI changes** /
 *    **Decline AI changes** pair. Both fire one push-batch each with the
 *    payload computed by `aiResolveLocally`.
 *  - CONFIRMED: existing **Edit** button (re-enters modify mode for the
 *    modify-confirmed re-export path). AI signals are resolved through
 *    Save & Confirm in modify mode, never inline outside it.
 *
 * In modify mode:
 *  - **Cancel** discards the user's buffered edits. AI suggestions stay
 *    pending — the user can still accept or decline them after exiting
 *    modify mode.
 *  - **Save** / **Save & Confirm**: single `buffer.flushSave()` call. The
 *    resolver merges user edits with pending AI blocks (user wins per
 *    field), the merged payload ships to push-batch, and the post-batch
 *    `pre_orders.status` decides whether the ERP re-export fires.
 */
export function ModifyControls({
  preOrder,
  buffer,
  variant = "default",
}: Readonly<Props>) {
  const { t } = useTranslation();
  // Flag still gates modify mode for CONFIRMED pre-orders (re-export to ERP).
  // PENDING / AI_PROPOSAL modify mode is always on.
  const confirmedModifyEnabled = useFeatureFlag("recap_modify_mode") === true;
  const aiActions = useAiPreOrderActions(preOrder.id);
  // Per-PO hard lock — covers in-flight save/AI/approve in this tab AND
  // server-side export.status ("in_progress" / "failed"). When locked,
  // every action button below is disabled so the user can't queue a
  // second mutation on top of an in-flight one or kick off Save while
  // the ConfirmConsumer is mid-export. Retry-from-failed lives on the
  // recap action bar (out of this component's scope).
  const lock = usePreOrderLockOptional();
  const goToPreOrder = useGoToPreOrder();
  const bufferStore = useModifyBufferStore();
  const { data: partners = [] } = useGlobalPartners();
  // Mutate gate — frontend mirror of the V2 API pre-order write authority
  // (`buildPreOrderVisibility` under `allConfirmedVisible:false`): a viewer
  // may accept / decline / modify AI changes only when they own the row,
  // share a group with it, or are a manager — the same predicate the
  // backend applies to pending-PO visibility. `recap_show_all_confirmed`
  // (Mode B) widens what an employee can SEE, never what they can edit, so
  // a non-owner viewing a foreign AI proposal must not be offered the
  // modify surface (the backend would 403 the click). While the groups
  // query is still loading we stay permissive — the V2 approve/decline +
  // push-batch endpoints enforce the same gate server-side regardless.
  const { viewer, isLoadingGroups } = useProposalViewer();
  const viewerCanMutate =
    isLoadingGroups || canMutatePreOrder(preOrder, viewer);
  // Map for resolving the post-accept partner name when an AI header
  // suggestion overlays `partner_id` — without this, `partnerName` would
  // stay bound to the OLD partner's name and the search-filter visibility
  // check would compare against the wrong partner.
  const resolvePartnerName = useMemo(() => {
    const byId = new Map<string, string>();
    for (const p of partners) {
      if (p?.id && typeof p.name === "string") byId.set(p.id, p.name);
    }
    return (id: string | null): string | undefined =>
      id ? byId.get(id) : undefined;
  }, [partners]);

  // Accept wrapper. The PO stays focused in the recap focus view, which
  // re-resolves the row from data after SSE lands — even an AI header
  // suggestion that swaps the partner doesn't need any list/filter nav
  // because we're not on the list view to begin with.
  const runApprove = async (): Promise<void> => {
    await aiActions.approveAll(preOrder);
  };

  const isPending =
    preOrder.status === PreOrderStatus.PENDING ||
    preOrder.status === PreOrderStatus.AI_PROPOSAL;
  const isConfirmed = preOrder.status === PreOrderStatus.CONFIRMED;
  if (!isPending && !isConfirmed) return null;
  if (isConfirmed && !confirmedModifyEnabled) return null;
  // Non-owner viewing a foreign row exposed by Mode B — read-only, no
  // modify / AI accept / decline affordances.
  if (!viewerCanMutate) return null;

  const hasPendingAi = hasAnyPendingAi(preOrder);
  // `aiBusy` already covers in-flight approve/decline from this tab. We
  // OR in `lock.isLocked` so the buttons also stay disabled when the
  // server reports a different action mid-flight (e.g. export in
  // progress under another tab, failed export awaiting retry) or while
  // a flush is pending — same surface for every reason the PO is
  // momentarily off-limits, no per-reason branching at the call sites.
  const aiBusy =
    aiActions.isApproving || aiActions.isDeclining || lock.isLocked;

  // Outside modify mode: render the AI bar for PENDING-like orders. For
  // CONFIRMED we render an asymmetric pair: Decline AI is one click
  // (declining stages no ERP work), but Accept AI requires entering modify
  // mode first — so the Edit button gets a violet Sparkles hint when AI is
  // pending. (PENDING with no AI relies on auto-enter-on-edit and renders
  // nothing here.)
  if (!buffer.isModifying) {
    if (isPending && hasPendingAi) {
      return (
        <AiActionsBar
          onAccept={runApprove}
          onDecline={() => aiActions.declineAll(preOrder)}
          isApproving={aiActions.isApproving}
          isDeclining={aiActions.isDeclining}
          variant={variant}
          t={t}
        />
      );
    }
    if (isPending) return null;
    return (
      <ConfirmedEntryBar
        hasPendingAi={hasPendingAi}
        onDecline={() => aiActions.declineAll(preOrder)}
        onEnterModify={() => buffer.enterModify()}
        isDeclining={aiActions.isDeclining}
        isApproving={aiActions.isApproving}
        t={t}
      />
    );
  }

  const { pendingInfo, lastError, isFlushing } = buffer;
  const hasPending = pendingInfo.total > 0;
  const hasError = !!lastError;

  // Flush typed-but-unfocused input values into the buffer on mousedown,
  // before React reads refs in the click handler.
  const blurActive = () => {
    const el = document.activeElement as HTMLElement | null;
    if (el && typeof el.blur === "function") el.blur();
  };

  // Single backend call. The push-batch service folds pending
  // `ai_suggestions[]` into the same transaction with user-edit priority,
  // and infers re-export from `pre_orders.status`. After a successful
  // flush, if the row had pending AI we shift the recap filters so the
  // post-accept partner remains visible (`computePostAcceptTarget` reads
  // the AI's `to` values from the pre-SSE snapshot).
  //
  // Error surface: `flushSave` swallows exceptions into
  // `bufferStore.lastError`. We clear before the call and read fresh
  // after — only navigate when the save actually committed.
  const runSave = async (): Promise<void> => {
    blurActive();
    const target = hasPendingAi
      ? computePostAcceptTarget(
          preOrder,
          collectAiPaths(preOrder),
          resolvePartnerName,
        )
      : null;
    bufferStore.setLastError(preOrder.id, null);
    await buffer.flushSave();
    if (target && bufferStore.getSlice(preOrder.id).lastError === null) {
      await goToPreOrder(target, { focusSku: false });
    }
  };

  return (
    <div
      className={cn(
        "flex items-center",
        variant === "hero" ? "gap-2" : "gap-1.5",
      )}
      data-testid="recap-modify-bar"
    >
      {/* Hero variant: Cancel is rendered separately by the focus hero
          action bar (far left, where the generic Delete button sits) so
          it is not crowded against Accept. The default bar keeps Cancel
          inline next to Save. */}
      {variant !== "hero" && (
        <Button
          size="sm"
          variant="outline"
          data-testid="recap-modify-cancel"
          data-attr="recap-modify-cancel"
          className={cn(
            BTN_SIZE[variant],
            "bg-red-50 hover:bg-red-100 text-red-700 border-red-300",
          )}
          disabled={isFlushing || aiBusy}
          onClick={(e) => {
            e.stopPropagation();
            buffer.cancelModify();
          }}
        >
          <X className={ICON_SIZE[variant]} />
          {t("ordersRecap.modifyCancel")}
        </Button>
      )}

      {hasError ? (
        <ModifyErrorPopover
          message={lastError?.message ?? ""}
          onRetry={runSave}
          isRetrying={false}
        >
          <Button
            size="sm"
            data-testid="recap-modify-failed"
            data-attr="recap-modify-failed"
            className={cn(
              BTN_SIZE[variant],
              "bg-red-50 hover:bg-red-100 text-red-700 border border-red-300",
            )}
          >
            <XCircle className={ICON_SIZE[variant]} />
            {t("ordersRecap.modifyFailed")}
            {hasPending && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[10px] font-semibold text-red-700">
                {pendingInfo.total}
              </span>
            )}
          </Button>
        </ModifyErrorPopover>
      ) : isPending ? (
        <SaveButton
          isBusy={isFlushing || aiBusy}
          hasPending={hasPending}
          hasPendingAi={hasPendingAi}
          pendingTotal={pendingInfo.total}
          onClick={runSave}
          onMouseDown={blurActive}
          variant={variant}
          t={t}
        />
      ) : (
        <ConfirmButton
          isBusy={isFlushing || aiBusy}
          hasPending={hasPending}
          hasPendingAi={hasPendingAi}
          pendingTotal={pendingInfo.total}
          onClick={runSave}
          onMouseDown={blurActive}
          t={t}
        />
      )}
    </div>
  );
}

interface AiActionsBarProps {
  onAccept: () => Promise<void>;
  onDecline: () => Promise<void>;
  isApproving: boolean;
  isDeclining: boolean;
  variant: ControlsVariant;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Accept/Decline pair shown outside modify mode for PENDING-like
 * pre-orders that carry any pending AI signal (header or flow level).
 *
 * `default` — compact violet Accept + violet-outline Decline.
 * `hero` — only the larger violet Accept. The destructive twin (the red
 * **Delete**) is rendered separately by the focus hero action bar in its
 * far-left slot, so it mirrors the modify-mode Cancel placement instead
 * of crowding against Accept.
 */
function AiActionsBar({
  onAccept,
  onDecline,
  isApproving,
  isDeclining,
  variant,
  t,
}: Readonly<AiActionsBarProps>) {
  // Same per-PO lock as the parent — also gates the outside-modify-mode
  // AI bar so the user can't fire approveAll/declineAll while the
  // server is exporting or recovering from a failed export. The
  // approval endpoint would 409 in those states; better to disable
  // the click than to surface the 409 toast.
  const lock = usePreOrderLockOptional();
  const busy = isApproving || isDeclining || lock.isLocked;
  const hero = variant === "hero";
  return (
    <div
      className={cn("flex items-center", hero ? "gap-2" : "gap-1.5")}
      data-testid="recap-ai-actions-bar"
    >
      {!hero && (
        <Button
          size="sm"
          variant="outline"
          data-testid="recap-ai-decline"
          data-attr="recap-ai-decline"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            void onDecline();
          }}
          className={cn(
            BTN_SIZE[variant],
            "bg-white hover:bg-violet-50 text-violet-700 border border-violet-300",
          )}
        >
          {isDeclining ? (
            <Loader2 className={cn(ICON_SIZE[variant], "animate-spin")} />
          ) : (
            <X className={ICON_SIZE[variant]} />
          )}
          {t("ordersRecap.aiProposal.declineChanges", "Decline AI changes")}
        </Button>
      )}
      <Button
        size="sm"
        data-testid="recap-ai-accept"
        data-attr="recap-ai-accept"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          void onAccept();
        }}
        className={cn(
          BTN_SIZE[variant],
          "bg-violet-600 hover:bg-violet-700 text-white border border-violet-700",
          hero && "font-semibold",
        )}
      >
        {isApproving ? (
          <Loader2 className={cn(ICON_SIZE[variant], "animate-spin")} />
        ) : (
          <Sparkles className={ICON_SIZE[variant]} />
        )}
        {t("ordersRecap.aiProposal.acceptChanges", "Accept AI changes")}
      </Button>
    </div>
  );
}

interface ConfirmButtonProps {
  isBusy: boolean;
  hasPending: boolean;
  hasPendingAi: boolean;
  pendingTotal: number;
  onClick: () => void;
  onMouseDown: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

/** Save & Confirm — for CONFIRMED parents this re-exports buffered edits via
 *  the modify-confirmed endpoint. When the order also carries pending AI it
 *  approves the suggestions in the same click (orchestrated server-side write
 *  → buffer flush). */
function ConfirmButton({
  isBusy,
  hasPending,
  hasPendingAi,
  pendingTotal,
  onClick,
  onMouseDown,
  t,
}: Readonly<ConfirmButtonProps>) {
  if (isBusy) {
    return (
      <Button
        size="sm"
        data-testid="recap-modify-confirm"
        data-attr="recap-modify-confirm"
        disabled
        className="h-6 gap-1 text-xs px-2 bg-gray-100 text-gray-600 border border-gray-300"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {t("ordersRecap.modifySaveAndConfirm")}
      </Button>
    );
  }

  if (!hasPending && !hasPendingAi) {
    return (
      <Button
        size="sm"
        data-testid="recap-modify-confirm"
        data-attr="recap-modify-confirm"
        disabled
        className="h-6 gap-1 text-xs px-2 bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
      >
        {t("ordersRecap.modifyNoChanges")}
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      data-testid="recap-modify-confirm"
      data-attr="recap-modify-confirm"
      className="h-6 gap-1 text-xs px-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
      onMouseDown={onMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {t("ordersRecap.modifySaveAndConfirm")}
      {hasPending && (
        <span
          data-testid="recap-modify-count"
          className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[10px] font-semibold text-orange-700 border border-orange-200"
        >
          {pendingTotal}
        </span>
      )}
      {hasPendingAi && (
        <Sparkles className="h-3 w-3 text-orange-500" aria-hidden="true" />
      )}
    </Button>
  );
}

interface SaveButtonProps {
  isBusy: boolean;
  hasPending: boolean;
  hasPendingAi: boolean;
  pendingTotal: number;
  onClick: () => void;
  onMouseDown: () => void;
  variant: ControlsVariant;
  t: ReturnType<typeof useTranslation>["t"];
}

/** PENDING-only save.
 *
 *  `default` — three visual modes:
 *   - busy: gray loader.
 *   - hasPending: orange Save (with optional violet sparkle when AI is also
 *     pending — Save will approve + flush).
 *   - !hasPending && hasPendingAi: violet **Accept AI** (no buffer to flush;
 *     click just runs approveAll).
 *   - otherwise hidden.
 *
 *  `hero` — a single orange **Accept & modify** button (the modify-mode
 *  twin of the violet **Accept**): accepts the AI proposal and flushes the
 *  user's buffered edits in one click. */
function SaveButton({
  isBusy,
  hasPending,
  hasPendingAi,
  pendingTotal,
  onClick,
  onMouseDown,
  variant,
  t,
}: Readonly<SaveButtonProps>) {
  const hero = variant === "hero";

  if (isBusy) {
    return (
      <Button
        size="sm"
        variant="outline"
        data-testid="recap-modify-save"
        data-attr="recap-modify-save"
        disabled
        className={cn(
          BTN_SIZE[variant],
          "bg-gray-100 text-gray-600 border border-gray-300",
        )}
      >
        <Loader2 className={cn(ICON_SIZE[variant], "animate-spin")} />
        {hero
          ? t("ordersRecap.aiProposal.acceptAndModify", "Accept & modify")
          : hasPending
            ? t("ordersRecap.modifySave")
            : t("ordersRecap.aiProposal.acceptChanges", "Accept AI changes")}
      </Button>
    );
  }

  // Hero (focus AI proposal, modify mode): one orange Accept & modify
  // button — picks up the modify-bar's orange so it reads as the
  // modify-mode counterpart of the violet Accept.
  if (hero) {
    return (
      <Button
        size="sm"
        variant="outline"
        data-testid="recap-modify-save"
        data-attr="recap-modify-save"
        data-variant="accept-modify"
        onMouseDown={onMouseDown}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={cn(
          BTN_SIZE.hero,
          "bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200 font-semibold",
        )}
      >
        <Sparkles className={ICON_SIZE.hero} />
        {t("ordersRecap.aiProposal.acceptAndModify", "Accept & modify")}
        {hasPending && (
          <span
            data-testid="recap-modify-save-count"
            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-white text-xs font-semibold text-orange-700 border border-orange-200"
          >
            {pendingTotal}
          </span>
        )}
      </Button>
    );
  }

  if (!hasPending && hasPendingAi) {
    return (
      <Button
        size="sm"
        data-testid="recap-modify-save"
        data-attr="recap-modify-save"
        data-variant="accept-ai"
        onMouseDown={onMouseDown}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="h-6 gap-1 text-xs px-2 bg-violet-600 hover:bg-violet-700 text-white border border-violet-700"
      >
        <Check className="h-3 w-3" />
        {t("ordersRecap.aiProposal.acceptChanges", "Accept AI changes")}
      </Button>
    );
  }

  if (!hasPending) return null;

  return (
    <Button
      size="sm"
      variant="outline"
      data-testid="recap-modify-save"
      data-attr="recap-modify-save"
      onMouseDown={onMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="h-6 gap-1 text-xs px-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
    >
      {t("ordersRecap.modifySave")}
      <span
        data-testid="recap-modify-save-count"
        className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[10px] font-semibold text-orange-700 border border-orange-200"
      >
        {pendingTotal}
      </span>
      {hasPendingAi && (
        <Sparkles className="h-3 w-3 text-violet-500" aria-hidden="true" />
      )}
    </Button>
  );
}

interface ConfirmedEntryBarProps {
  hasPendingAi: boolean;
  onDecline: () => Promise<void>;
  onEnterModify: () => void;
  isDeclining: boolean;
  isApproving: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}

/** Outside-modify-mode bar for CONFIRMED pre-orders.
 *
 *  - No pending AI: just the blue **Edit** button (legacy behaviour).
 *  - Pending AI: a violet **Decline AI changes** button (one-click decline,
 *    no modify-mode entry needed) + an **Edit** button with a violet
 *    Sparkles hint and ring to point the user at the gesture that accepts:
 *    enter modify mode → Save & Confirm orchestrates `approveAll` + flush
 *    + re-export through `ConfirmButton`. */
function ConfirmedEntryBar({
  hasPendingAi,
  onDecline,
  onEnterModify,
  isDeclining,
  isApproving,
  t,
}: Readonly<ConfirmedEntryBarProps>) {
  // See AiActionsBar for the rationale — refuse Decline AI while the
  // server is mid-export, and refuse Enter Modify (would let the user
  // start typing edits that would race the ConfirmConsumer's M3 push
  // or the failed-export recovery).
  const lock = usePreOrderLockOptional();
  const aiBusy = isApproving || isDeclining || lock.isLocked;
  const editClass = hasPendingAi
    ? "h-6 gap-1 text-xs px-2 border-violet-500 text-violet-700 hover:bg-violet-50 hover:text-violet-800 hover:border-violet-600 font-semibold ring-1 ring-violet-200"
    : "h-6 gap-1 text-xs px-2 border-blue-500 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-600 font-semibold";
  const editTitle = hasPendingAi
    ? t(
        "ordersRecap.aiProposal.enterModifyToAccept",
        "Pending AI suggestions — enter modify mode to accept",
      )
    : undefined;
  return (
    <div
      className="flex items-center gap-1.5"
      data-testid="recap-confirmed-entry-bar"
    >
      {hasPendingAi && (
        <Button
          size="sm"
          variant="outline"
          data-testid="recap-ai-decline"
          data-attr="recap-ai-decline"
          disabled={aiBusy}
          onClick={(e) => {
            e.stopPropagation();
            void onDecline();
          }}
          className="h-6 gap-1 text-xs px-2 bg-white hover:bg-violet-50 text-violet-700 border border-violet-300"
        >
          {isDeclining ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
          {t("ordersRecap.aiProposal.declineChanges", "Decline AI changes")}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        data-testid="recap-modify-enter"
        data-attr="recap-modify-enter"
        title={editTitle}
        // Lock-aware: can't enter modify mode while the server is
        // busy (export in progress / failed). Entering would let the
        // user start typing edits that would race the ConfirmConsumer
        // callback when it lands.
        disabled={lock.isLocked}
        className={editClass}
        onClick={(e) => {
          e.stopPropagation();
          onEnterModify();
        }}
      >
        {hasPendingAi && (
          <Sparkles className="h-3 w-3 text-violet-500" aria-hidden="true" />
        )}
        <Pencil className="h-3 w-3" />
        {t("ordersRecap.modify")}
      </Button>
    </div>
  );
}
